import { HDate, Molad } from '@hebcal/core';

/**
 * Compute Shabbat-specific prayer additions.
 *
 * @param {HDate} hdate
 * @param {object} calData - from calendarProvider
 * @param {object} tachanun - from tachanunRules
 * @param {string} edah - 'ashkenaz' | 'sephardic' | 'mizrahi'
 * @returns {object}
 */
export function computeShabbatAdditions(hdate, calData, tachanun, edah) {
  if (!calData.isShabbat) {
    return {
      avHarachamim: false,
      tzidkatcha: false,
      shabbatMevarchim: false,
      mevarchimMonth: null,
    };
  }

  const m = hdate.getMonth();

  // ═══ Av HaRachamim ═══
  const avHarachamim = computeAvHarachamim(hdate, calData, tachanun);

  // ═══ Tzidkatcha Tzedek ═══
  const tzidkatcha = computeTzidkatcha(hdate, calData, tachanun, edah);

  // ═══ Shabbat Mevarchim ═══
  // Already computed by calendarProvider, just pass through
  // Exception: no birkat hachodesh before Tishrei (already handled by hebcal)
  const shabbatMevarchim = calData.shabbatMevarchim;
  const mevarchimMonth = calData.mevarchimMonth;

  // ═══ Molad for Shabbat Mevarchim ═══
  // Compute the precise molad of the upcoming month using @hebcal/core
  let mevarchimMolad = null;
  if (shabbatMevarchim) {
    mevarchimMolad = computeMevarchimMolad(hdate);
  }

  return {
    avHarachamim,
    tzidkatcha,
    shabbatMevarchim,
    mevarchimMonth,
    mevarchimMolad,
  };
}

/**
 * Av HaRachamim — say or skip matrix.
 *
 * DEFAULT: Say
 * SKIP when:
 * - Shabbat RC
 * - Shabbat Mevarchim (EXCEPT Mevarchim Iyar and Mevarchim Sivan — sefirah period)
 * - 4 Parshiyot (Shekalim, Zachor, Parah, HaChodesh)
 * - Shabbat Chanukah
 * - Shabbat Chol HaMoed
 * - Shabbat YT
 * - Shabbat during all of Nisan
 *
 * SAY (even on Mevarchim):
 * - Shabbat Chazon (before 9 Av)
 * - Shabbat Mevarchim Iyar (omer period)
 * - Shabbat Mevarchim Sivan (omer period)
 */
function computeAvHarachamim(hdate, calData, tachanun) {
  const m = hdate.getMonth();

  // Skip for Yom Tov / Chol HaMoed
  if (calData.isYomTov || calData.isCholHamoed) return false;

  // Skip for Rosh Chodesh
  if (calData.isRoshChodesh) return false;

  // Skip for Chanukah
  if (calData.isChanukah) return false;

  // Skip for all of Nisan (no tachanun all month)
  if (m === 1) return false;

  // 4 Parshiyot — skip
  if (calData.specialShabbat) {
    const spec = calData.specialShabbat;
    if (spec.includes('שקלים') || spec.includes('זכור') ||
        spec.includes('פרה') || spec.includes('החודש')) {
      return false;
    }
  }

  // Shabbat Mevarchim — skip, EXCEPT Iyar and Sivan (omer mourning)
  if (calData.shabbatMevarchim) {
    const month = calData.mevarchimMonth;
    if (month && (month.includes('אִיָּיר') || month.includes('אייר') ||
                  month.includes('סִיוָן') || month.includes('סיוון'))) {
      return true; // Exception: SAY during sefirah
    }
    return false; // Skip for other months
  }

  // Default: say
  return true;
}

/**
 * Compute the molad (new moon) for Shabbat Mevarchim announcement.
 *
 * Uses @hebcal/core Molad class for precise calculation in the traditional
 * synagogue announcement format: day of week, hour, minutes, and chalakim.
 *
 * @param {HDate} hdate - current Shabbat date
 * @returns {object} molad data for display
 */
function computeMevarchimMolad(hdate) {
  const year = hdate.getFullYear();
  const month = hdate.getMonth();

  // The upcoming month to bless: usually current month + 1
  let nextMonth = month + 1;
  let nextYear = year;

  // Handle year boundary (Elul → Tishrei not announced, but just in case)
  const isLeapYear = HDate.isLeapYear(year);
  const lastMonth = isLeapYear ? 13 : 12;
  if (nextMonth > lastMonth) {
    nextMonth = 7; // Tishrei
    nextYear = year + 1;
  }

  try {
    const molad = new Molad(nextYear, nextMonth);
    const DOW_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

    const dow = molad.getDow();
    const hour = molad.getHour();
    const minutes = molad.getMinutes();
    const chalakim = molad.getChalakim();

    // Format as announced in synagogue:
    // "המולד יהיה ביום X בשעה Y ו-Z דקות ו-W חלקים"
    let formatted = `יום ${DOW_NAMES[dow]}, שעה ${hour}`;
    if (minutes > 0 || chalakim > 0) {
      formatted += ` ו-${minutes} דקות`;
    }
    if (chalakim > 0) {
      formatted += ` ו-${chalakim} חלקים`;
    }

    return {
      dow,
      dowName: DOW_NAMES[dow],
      hour,
      minutes,
      chalakim,
      formatted,
      renderedHe: molad.render('he'),
    };
  } catch (err) {
    console.warn('Molad computation failed:', err.message);
    return null;
  }
}

/**
 * Tzidkatcha Tzedek — say or skip.
 *
 * Skip when no tachanun on weekday.
 * Edah difference: Ashkenazim skip on Shabbat RC, Sephardim say it.
 */
function computeTzidkatcha(hdate, calData, tachanun, edah) {
  // Skip when weekday tachanun would be skipped
  if (!tachanun.shacharit) return false;

  // Shabbat RC — edah split
  if (calData.isRoshChodesh) {
    return edah === 'sephardic' || edah === 'mizrahi'; // Sephardim say it
  }

  return true;
}
